import { useEffect, useState } from "react";
import { Check, PlayCircle, X } from "lucide-react";
import { api } from "../../lib/api";
import { useTour } from "./TourContext";

const SENIOR = ["owner", "manager", "captain"];

const ITEMS = [
  { key: "has_yacht", label: "Add your first yacht", tour: "add-yacht" },
  { key: "has_crew", label: "Invite your crew", tour: "invite-crew" },
  { key: "has_custodian", label: "Appoint a petty-cash custodian", tour: "petty-cash" },
  { key: "has_inventory", label: "Add inventory items", tour: "add-inventory" },
  { key: "has_checklists", label: "Set up checklists", tour: "checklist" },
  { key: "has_task", label: "Create your first task", tour: "create-task" },
  { key: "has_logbook", label: "Record a logbook entry", tour: "logbook-entry" },
];

/**
 * "Getting started" progress card on the Dashboard. Shown to senior roles
 * until every setup step is done (or the card is dismissed for this
 * user+yacht). Each open step links straight into its guided tour.
 */
export default function GettingStarted({ yachtId, role, userId }) {
  const { startTour } = useTour();
  const [status, setStatus] = useState(null);
  const [dismissed, setDismissed] = useState(true);
  const storageKey = `seavanto_gs_dismissed_${userId}_${yachtId}`;

  useEffect(() => {
    setDismissed(!!localStorage.getItem(storageKey));
  }, [storageKey]);

  useEffect(() => {
    if (!yachtId || !SENIOR.includes(role)) return;
    let cancelled = false;
    api
      .get(`/onboarding/status?yacht_id=${yachtId}`)
      .then((r) => { if (!cancelled) setStatus(r.data); })
      .catch(() => { if (!cancelled) setStatus(null); });
    return () => { cancelled = true; };
  }, [yachtId, role]);

  if (!SENIOR.includes(role) || dismissed || !status) return null;
  const done = ITEMS.filter((i) => status[i.key]).length;
  if (done === ITEMS.length) return null;

  return (
    <div className="mt-8 rounded-lg border border-zinc-200 bg-white p-5" data-testid="getting-started-card">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Getting started</div>
          <div className="font-display text-lg font-medium mt-0.5">
            Set up your yacht — {done} of {ITEMS.length} done
          </div>
        </div>
        <button
          type="button"
          onClick={() => { localStorage.setItem(storageKey, "1"); setDismissed(true); }}
          className="text-zinc-400 hover:text-zinc-700 p-1 rounded"
          aria-label="Dismiss getting started"
          data-testid="getting-started-dismiss"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="mt-3 h-1.5 rounded-full bg-zinc-100 overflow-hidden">
        <div
          className="h-full bg-ink rounded-full transition-all duration-500"
          style={{ width: `${(done / ITEMS.length) * 100}%` }}
          data-testid="getting-started-progress"
        />
      </div>
      <ul className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1.5">
        {ITEMS.map((it) => {
          const isDone = !!status[it.key];
          return (
            <li
              key={it.key}
              className="flex items-center gap-2.5 py-1"
              data-testid={`gs-item-${it.tour}`}
            >
              <span
                className={`grid place-items-center h-5 w-5 rounded-full border shrink-0 ${
                  isDone ? "bg-emerald-600 border-emerald-600 text-white" : "border-zinc-300 text-transparent"
                }`}
              >
                <Check className="h-3 w-3" />
              </span>
              <span className={`text-sm flex-1 ${isDone ? "text-zinc-400 line-through" : "text-zinc-800"}`}>
                {it.label}
              </span>
              {!isDone && (
                <button
                  type="button"
                  onClick={() => startTour(it.tour)}
                  className="inline-flex items-center gap-1 text-[12px] text-zinc-600 hover:text-ink border border-zinc-200 hover:border-zinc-300 rounded-md px-2 py-1 transition-colors"
                  data-testid={`gs-start-${it.tour}`}
                >
                  <PlayCircle className="h-3.5 w-3.5" /> Show me
                </button>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
